import { GooglePlacesLoadObserver } from './GooglePlacesLoadObserver';

const VALID_PLACES_STATUSES = ['OK', 'ZERO_RESULTS'];

/**
 * Extracts ISO 3166-1 Alpha-2 country code from PlaceResult address_components.
 * This is the only reliable way to determine country without locale-dependent string matching.
 *
 * @param place - Google Places PlaceResult from getPlaceDetails
 * @returns ISO country code (e.g., 'BR', 'US') or null if not found
 */
const getCountryCodeFromPlaceResult = (
    place: google.maps.places.PlaceResult,
): string | null => {
    if (!place.address_components) {
        return null;
    }

    const countryComponent = place.address_components.find((component) =>
        component.types.includes('country'),
    );

    return countryComponent?.short_name || null;
};

/**
 * Simple in-memory cache for place details to avoid redundant API calls.
 * Key: place_id, Value: PlaceResult
 */
const placeDetailsCache = new Map<string, google.maps.places.PlaceResult>();

const CACHE_MAX_SIZE = 100; // Limit cache size

const getCachedPlaceDetails = (
    placeId: string,
): google.maps.places.PlaceResult | null => {
    return placeDetailsCache.get(placeId) || null;
};

const setCachedPlaceDetails = (
    placeId: string,
    place: google.maps.places.PlaceResult,
): void => {
    // Simple LRU: remove oldest entry if cache is full
    if (placeDetailsCache.size >= CACHE_MAX_SIZE) {
        const firstKey = placeDetailsCache.keys().next().value;
        placeDetailsCache.delete(firstKey);
    }
    placeDetailsCache.set(placeId, place);
};

/**
 * Filters out locations from blacklisted countries by fetching place details
 * to get accurate country codes. Uses caching and parallel API calls for performance.
 *
 * @param predictions - Array of AutocompletePredictions from Google Places API
 * @param blacklistedCountries - Set of ISO 3166-1 Alpha-2 country codes to filter out
 * @returns Filtered array with blacklisted country locations removed
 */
const filterBlacklistedCountries = async (
    predictions: google.maps.places.AutocompletePrediction[],
    blacklistedCountries: Set<string>,
): Promise<google.maps.places.AutocompletePrediction[]> => {
    if (!predictions || predictions.length === 0) {
        return predictions;
    }

    // Early return if no countries are blacklisted
    if (blacklistedCountries.size === 0) {
        return predictions;
    }

    // Fetch place details for all predictions in parallel
    // Check cache first to avoid redundant API calls
    const placeDetailsPromises = predictions.map(async (prediction) => {
        // Check cache first
        const cached = getCachedPlaceDetails(prediction.place_id);
        if (cached) {
            return cached;
        }

        // Fetch from API
        try {
            const placeDetails = await getPlaceDetails(prediction.place_id);
            if (placeDetails) {
                setCachedPlaceDetails(prediction.place_id, placeDetails);
            }
            return placeDetails;
        } catch {
            return null;
        }
    });

    const placeDetailsResults = await Promise.all(placeDetailsPromises);

    // Filter out locations from blacklisted countries using country codes
    return predictions.filter((_prediction, index) => {
        const placeDetails = placeDetailsResults[index];
        if (!placeDetails) {
            // Fail open: if we can't get details, keep the prediction
            return true;
        }

        const countryCode = getCountryCodeFromPlaceResult(placeDetails);
        if (!countryCode) {
            // Fail open: if country code is missing, keep the prediction
            return true;
        }

        // Filter out if country is in blacklist
        return !blacklistedCountries.has(countryCode);
    });
};

interface GetPlacePredictionsOptions {
    blacklistedCountries?: Set<string>;
    [key: string]: any;
}

/**
 * getPlacePredictions calls the `getPlacePredictions` method on the
 * AutocompleteService object in the GoogleMaps API, and returns a promise
 * representing success or failure. See:
 * https://developers.google.com/maps/documentation/javascript/reference#AutocompleteService
 *
 * @param searchTerm - Search query string
 * @param options - Options object containing blacklistedCountries Set and other Google Places API options
 * @returns Promise resolving to filtered AutocompletePredictions or null
 */
export const getPlacePredictions = (
    searchTerm: string,
    options: GetPlacePredictionsOptions = {},
): Promise<google.maps.places.AutocompletePrediction[] | null> => {
    const { blacklistedCountries = new Set<string>(), ...googlePlacesOptions } =
        options;

    const autocompletionRequest = {
        input: searchTerm,
        ...googlePlacesOptions,
    };

    return GooglePlacesLoadObserver.get().then(
        async () =>
            new Promise((resolve, reject) => {
                const autocompleteService =
                    new window.google.maps.places.AutocompleteService();

                autocompleteService.getPlacePredictions(
                    autocompletionRequest,
                    async (suggestions, status) => {
                        if (VALID_PLACES_STATUSES.includes(status)) {
                            try {
                                // Filter out blacklisted countries using accurate country codes
                                // Empty Set will early return in filterBlacklistedCountries
                                const filteredSuggestions = suggestions
                                    ? await filterBlacklistedCountries(
                                          suggestions,
                                          blacklistedCountries,
                                      )
                                    : null;
                                resolve(filteredSuggestions);
                            } catch (error) {
                                // Fail open: if filtering fails, return original results
                                console.warn(
                                    'Failed to filter blacklisted countries:',
                                    error,
                                );
                                resolve(suggestions);
                            }
                        } else {
                            reject(status);
                        }
                    },
                );
            }),
    );
};

/**
 * getPlaceDetails calls the `getDetails` method on the PlacesService object in
 * the GoogleMaps API, and returns a promise representing success or failure. See:
 *
 * https://developers.google.com/maps/documentation/javascript/reference#PlacesService
 *
 */
export const getPlaceDetails = (
    placeId: string,
): Promise<google.maps.places.PlaceResult | null> =>
    GooglePlacesLoadObserver.get().then(
        () =>
            new Promise((resolve, reject) => {
                // an HTMLElement is required, because the Google ToS disallows
                // getting details about a place without displaying it on a map.
                const placesService =
                    new window.google.maps.places.PlacesService(
                        document.createElement('div'),
                    );

                placesService.getDetails({ placeId }, (result, status) => {
                    if (VALID_PLACES_STATUSES.includes(status)) {
                        resolve(result);
                    } else {
                        reject(status);
                    }
                });
            }),
    );

/**
 * geocodeAddress calls the `Geocoder.geocode` method in the GoogleMaps API,
 * and returns a promise representing success or failure. Primary purpose is to
 * get a lat/lon value from an address. See:
 *
 * https://developers.google.com/maps/documentation/javascript/reference#Geocoder
 *
 **/
export const geocodeAddress = (
    address: string | null | undefined,
): Promise<google.maps.GeocoderResult[] | null> =>
    GooglePlacesLoadObserver.get().then(
        () =>
            new Promise((resolve, reject) => {
                const geocoder = new window.google.maps.Geocoder();

                geocoder.geocode({ address }, (result, status) => {
                    if (VALID_PLACES_STATUSES.includes(status)) {
                        resolve(result);
                    } else {
                        reject(status);
                    }
                });
            }),
    );
